import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import {
    type Control,
    Controller,
    type FieldErrors,
    type UseFormRegister,
    type UseFormSetValue,
    type UseFormWatch,
} from 'react-hook-form';

import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import Input from '@/components/ui/Input';
import SelectInput from '@/components/ui/SelectInput';
import { useCountryNames } from '@/hooks/useCountryNames';
import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

type CompanySectionProps = {
    register: UseFormRegister<InvoiceDataSchemaType>;
    control: Control<InvoiceDataSchemaType>;
    watch: UseFormWatch<InvoiceDataSchemaType>;
    errors: FieldErrors<InvoiceDataSchemaType>;
    setValue: UseFormSetValue<InvoiceDataSchemaType>;
    state: InvoiceDataSchemaType;
};

export const CompanySection = ({ register, control, errors, state }: CompanySectionProps) => {
    const countryNames = useCountryNames;
    const { isRegistrationNumberRequired } = useUserStore.getState();
    const showAdditionalAddress = !!state.company.address2;

    const getFieldProps = (keyName: keyof InvoiceDataSchemaType['company']) => ({
        ...register(`company.${keyName}`),
        disabled: !!state.company[keyName],
    });

    return (
        <div className='margin-bottom-15 margin-top-25 rounded bg-white padding-25'>
            <div className='text-bold text-size-18 padding-bottom-15'>
                {t('onboarding.personalInvoiceInformation.title')}
            </div>
            <div className='padding-bottom-15'>{t('onboarding.personalInvoiceInformation.description')}</div>

            <div className='display-flex gap-15'>
                <Input
                    required
                    className='flex-1-1-0 margin-bottom-15'
                    label={t('general.companyName')}
                    error={errors?.company?.customerName?.message}
                    inputAttributes={getFieldProps('customerName')}
                />
                {isRegistrationNumberRequired && (
                    <Input
                        required
                        className='flex-1-1-0'
                        label={t('general.companyRegistrationNumber')}
                        error={errors?.company?.companyRegistrationNumber?.message}
                        inputAttributes={{
                            ...register('company.companyRegistrationNumber'),
                        }}
                    />
                )}
            </div>
            <Input
                required
                className='margin-bottom-15'
                label={t('general.address')}
                error={errors?.company?.address?.message}
                inputAttributes={getFieldProps('address')}
            />

            {showAdditionalAddress && (
                <Input
                    className='margin-bottom-15'
                    label={t('general.addressOptional')}
                    inputAttributes={{ ...register('company.address2'), disabled: true }}
                />
            )}

            <div className='display-flex gap-15'>
                <Input
                    required
                    className='flex-1-1-0 margin-bottom-0'
                    label={t('general.postCode')}
                    error={errors?.company?.postCode?.message}
                    inputAttributes={getFieldProps('postCode')}
                />
                <Input
                    required
                    className='flex-1-1-0 margin-bottom-0'
                    label={t('general.city')}
                    error={errors?.company?.city?.message}
                    inputAttributes={getFieldProps('city')}
                />
                <Controller
                    name='company.country'
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <SelectInput
                            required
                            className='flex-1-1-0 margin-bottom-0'
                            error={errors.company?.country?.message}
                            label={t('general.country')}
                            selectAttributes={{
                                disabled: state.company.country === value,
                                onChange: (item?: SelectOption) => onChange(item?.id),
                                options: countryNames(value),
                            }}
                        />
                    )}
                />
            </div>
        </div>
    );
};

export default CompanySection;
